import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowLeft, LayoutGrid, Inbox, Briefcase, Building2, BarChart3, Megaphone, Building, Wrench } from "lucide-react";
import DemoConsole from "./DemoConsole";
import { PERSPECTIVES, SSAProvider, perspectiveFromPath } from "./SSAContext";

const NAV = [
  { to: "/services", label: "All categories", icon: LayoutGrid, end: true, group: "Buyer" },
  { to: "/services/c/DM", label: "Digital Marketing", icon: Megaphone, group: "Buyer" },
  { to: "/services/c/FM", label: "Facilities Management", icon: Building, group: "Buyer" },
  { to: "/services/requests", label: "My service requests", icon: Inbox, group: "Buyer" },
  { to: "/services/workbench", label: "Procurement Workbench", icon: Briefcase, group: "Infosys Procurement" },
  { to: "/services/insights", label: "Insights & reporting", icon: BarChart3, group: "Infosys Procurement" },
  ...["A", "B", "C", "D"].map((p) => ({ to: `/services/partner/${p}`, label: `Partner ${p} workspace`, icon: Building2, group: "Digital Marketing partners (demo)" })),
  ...["E", "F", "G", "H"].map((p) => ({ to: `/services/partner/${p}`, label: `Partner ${p} workspace`, icon: Wrench, group: "IFM partners (demo)" })),
];

function Sidebar() {
  const groups = [...new Set(NAV.map((n) => n.group))];
  return (
    <aside className="hidden lg:block w-60 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-120px)]" data-testid="ssa-sidebar">
      <div className="p-4 space-y-6 sticky top-0">
        {groups.map((g) => (
          <div key={g}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mb-2 px-2">{g}</div>
            {NAV.filter((n) => n.group === g).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`ssa-nav-${n.to.split("/").filter(Boolean).join("-")}`}
                className={({ isActive }) => `flex items-center gap-2.5 px-2 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-[#0B2A4A] text-white" : "text-slate-700 hover:bg-slate-100"}`}
              >
                <n.icon size={15} /> {n.label}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-500 leading-relaxed">
          Live categories: Digital Marketing and Integrated FM. Next on the same framework: Legal, Translation, Market Research.
        </div>
      </div>
    </aside>
  );
}

function ViewBanner() {
  const loc = useLocation();
  const p = PERSPECTIVES.find((x) => x.key === perspectiveFromPath(loc.pathname));
  const tone = p.key === "buyer" ? "bg-sky-50 text-sky-900 border-sky-200" : p.key === "procurement" ? "bg-indigo-50 text-indigo-900 border-indigo-200" : "bg-emerald-50 text-emerald-900 border-emerald-200";
  return (
    <div className={`border-b ${tone} text-[11px] font-bold uppercase tracking-[0.2em] px-6 py-1.5 flex items-center justify-between`} data-testid="ssa-view-banner">
      <span>Demo view: {p.label}</span>
      <span className="normal-case tracking-normal font-medium opacity-80 hidden md:inline">Perspective switch only – same signed-in session, not an identity change</span>
    </div>
  );
}

export default function SSALayout() {
  return (
    <SSAProvider>
      <div className="min-h-screen bg-[#F4F6F9]" data-testid="ssa-shell">
        <header className="bg-[#0B2A4A] text-white">
          <div className="max-w-[1500px] mx-auto px-4 lg:px-6 h-14 flex items-center gap-4">
            <Link to="/infoshop" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs" data-testid="ssa-back-to-catalog">
              <ArrowLeft size={14} /> UniBuy™ catalog
            </Link>
            <span className="h-6 w-px bg-white/20" />
            <img src="/unibuy-wordmark.png" alt="UniBuy™" className="h-7 w-auto bg-white rounded px-1.5 py-0.5" />
            <div className="leading-tight">
              <div className="font-display font-semibold text-sm">Strategic Services Aggregator</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#7DD3FC]">Digital Marketing · Integrated Facilities Management</div>
            </div>
          </div>
        </header>
        <DemoConsole />
        <ViewBanner />
        <div className="max-w-[1500px] mx-auto flex">
          <Sidebar />
          <main className="flex-1 min-w-0 p-4 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </SSAProvider>
  );
}
